/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Fullscreen media viewer (Figma desktop 4036:28876 / 4036:29106).
// Replaces the single-image `ImageLightbox` with a carousel that previews
// a GROUP of media (or a single item) from a conversation:
//   - a top-left sender header (avatar + name + timestamp),
//   - centered image (contain) OR inline <video controls> on web,
//   - prev / next chevrons (when >1 item),
//   - a bottom-left thumbnail strip (active item ringed; click to jump),
//   - a bottom-right toolbar: download, forward, share, and a "more
//     options" (…) popover (Reply / Copy image / Go to message / Delete),
//   - a top-right close (X).
//
// All chrome glyphs use the EXACT Figma SVGs via `MediaPreviewIcon`
// (web data-URI <Image>, macOS SvgXml) — no lucide approximations.
//
// Single VideoAttachmentCard plays INLINE in the timeline; this screen is
// opened only when the user hits the card's fullscreen control, so inline
// playback stays intact.
//
// Web-first: rendered through a body `WebPortal` for instant paint + the
// `<video>`/popover chrome. Native falls back to a centered image + close
// (RN `Modal`); the carousel/video controls are no-ops there for now.

import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Image, Modal, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { FileText } from 'lucide-react-native';
import { MediaPreviewIcon } from './MediaPreviewIcon';
import { WebPortal } from './composer/WebPortal';
import { PingVideoPlayer } from '../native/PingVideoPlayer';
import { PingDocReader } from '../native/PingDocReader';

const IS_WEB = typeof document !== 'undefined' && typeof window !== 'undefined';

// Figma colours: chrome strokes #E8EAED, popover surface #15171C, hairline
// border #2F333B, row label #F5F6F8, destructive #FF5353.
const CHROME = '#E8EAED';
const DESTRUCTIVE = '#FF5353';

export interface MediaPreviewItem {
  readonly id: string;
  readonly kind: 'image' | 'video' | 'doc';
  /** Directly-viewable URL (image src / decrypted video or PDF object URL). */
  readonly uri?: string | undefined;
  /** Video poster / doc first-page thumbnail (shown until the clip loads
   *  and as the strip thumbnail). */
  readonly posterUri?: string | undefined;
  /** Document file name (shown in the reader header / fallback). */
  readonly filename?: string | undefined;
  /** Document MIME — decides inline reader vs download fallback. */
  readonly mime?: string | undefined;
  /** Sender header (Figma top-left) — name + avatar + timestamp. */
  readonly senderName?: string | undefined;
  readonly senderAvatarUri?: string | undefined;
  readonly senderInitials?: string | undefined;
  readonly timestampLabel?: string | undefined;
}

export interface MediaPreviewScreenProps {
  readonly open: boolean;
  readonly items: readonly MediaPreviewItem[];
  readonly index: number;
  readonly onIndexChange: (next: number) => void;
  readonly onClose: () => void;
  /** Toolbar actions — omitted ones hide their control. */
  readonly onDownload?: ((item: MediaPreviewItem) => void) | undefined;
  readonly onForward?: ((item: MediaPreviewItem) => void) | undefined;
  readonly onOpenExternal?: ((item: MediaPreviewItem) => void) | undefined;
  /** "More options" popover actions. */
  readonly onReply?: ((item: MediaPreviewItem) => void) | undefined;
  readonly onCopy?: ((item: MediaPreviewItem) => void) | undefined;
  readonly onGoToMessage?: ((item: MediaPreviewItem) => void) | undefined;
  readonly onDelete?: ((item: MediaPreviewItem) => void) | undefined;
}

export const MediaPreviewScreen = React.memo<MediaPreviewScreenProps>((props) => {
  const { open, items, index, onIndexChange, onClose } = props;
  const { width: winW, height: winH } = useWindowDimensions();
  const [menuOpen, setMenuOpen] = useState(false);
  // Track which item's media (image/video) failed to load so a broken/expired
  // URL shows an inline error instead of a silent blank stage. Keyed by item
  // id so navigating to another item clears the error naturally.
  const [erroredId, setErroredId] = useState<string | null>(null);

  const count = items.length;
  const safeIndex = Math.min(Math.max(index, 0), Math.max(0, count - 1));
  const active = items[safeIndex];

  const goPrev = useCallback(() => {
    if (count <= 1) return;
    onIndexChange((safeIndex - 1 + count) % count);
    setMenuOpen(false);
  }, [count, safeIndex, onIndexChange]);
  const goNext = useCallback(() => {
    if (count <= 1) return;
    onIndexChange((safeIndex + 1) % count);
    setMenuOpen(false);
  }, [count, safeIndex, onIndexChange]);

  // Keyboard: ←/→ navigate, Esc closes (web only).
  useEffect(() => {
    if (!open || !IS_WEB) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, goPrev, goNext]);

  if (!open || active === undefined) return null;

  const hasHeader = active.senderName !== undefined && active.senderName.length > 0;

  const stage = (
    <Pressable
      accessibilityRole="none"
      onPress={onClose}
      className="flex-1 items-center justify-center bg-black/95"
    >
      {/* Centred media — swallow the press so it doesn't dismiss. */}
      <Pressable
        onPress={(e) => (e as { stopPropagation?: () => void }).stopPropagation?.()}
        className="items-center justify-center"
        style={{ maxWidth: winW, maxHeight: winH }}
      >
        {active.kind === 'doc' && active.uri !== undefined && IS_WEB ? (
          docRendersInline(active.mime, active.filename) ? (
            // Browser-native reader for anything the UA can display inline
            // (PDF, text, images, HTML/SVG). The decrypted object URL renders
            // in an <iframe> with the UA's built-in scroll/zoom/page controls.
            <iframe
              key={active.id}
              src={active.uri}
              title={active.filename ?? 'Document'}
              style={{
                width: winW * 0.78,
                height: winH * 0.88,
                border: 'none',
                background: '#fff',
                borderRadius: 8,
              }}
            />
          ) : (
            // Non-inline doc (docx/xlsx/zip…): the browser can't render it,
            // so show a card prompting Download (toolbar below has the action).
            <View className="items-center justify-center gap-4 px-6 py-10 rounded-[12px] bg-[#15171C]">
              <FileText size={64} strokeWidth={1.5} color={CHROME} />
              <Text numberOfLines={2} className="font-sans text-[16px] leading-[22px] text-[#F5F6F8] text-center">
                {active.filename ?? 'Document'}
              </Text>
              <Text className="font-sans text-[14px] leading-[19px] text-white/60 text-center">
                Preview isn't available for this file type.{'\n'}Use Download to open it.
              </Text>
            </View>
          )
        ) : active.kind === 'video' && active.uri !== undefined && IS_WEB ? (
          erroredId === active.id ? (
            <MediaLoadError noun="video" />
          ) : (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              key={active.id}
              src={active.uri}
              poster={active.posterUri}
              controls
              autoPlay
              playsInline
              onError={() => setErroredId(active.id)}
              style={{
                maxWidth: winW * 0.9,
                maxHeight: winH * 0.9,
                background: '#000',
                objectFit: 'contain',
              }}
            />
          )
        ) : active.kind === 'video' && active.uri !== undefined && !IS_WEB ? (
          // macOS: native AVKit player (source must be a local file path —
          // the host stages decrypted bytes via `stageVideoTempFile`).
          <PingVideoPlayer
            source={active.uri}
            autoplay
            controls
            style={{ width: winW * 0.9, height: winH * 0.9 }}
          />
        ) : active.kind === 'doc' && active.uri !== undefined && !IS_WEB ? (
          // macOS: native PDFKit / NSTextView reader (local file path).
          <PingDocReader
            source={active.uri}
            {...(active.mime !== undefined ? { mime: active.mime } : {})}
            {...(active.filename !== undefined ? { filename: active.filename } : {})}
            style={{ width: winW * 0.78, height: winH * 0.88 }}
          />
        ) : active.kind !== 'doc' && active.uri !== undefined ? (
          erroredId === active.id ? (
            <MediaLoadError noun="image" />
          ) : (
            <Image
              source={{ uri: active.uri }}
              resizeMode="contain"
              accessibilityIgnoresInvertColors
              onError={() => setErroredId(active.id)}
              style={{ width: winW * 0.92, height: winH * 0.88 }}
            />
          )
        ) : (
          // No resolvable URL yet (blob still decrypting) → centered spinner
          // instead of a black stage / silent null.
          <View
            className="items-center justify-center"
            style={{ width: winW * 0.5, height: winH * 0.5 }}
          >
            <ActivityIndicator size="large" color={CHROME} />
          </View>
        )}
      </Pressable>

      {/* Top-left sender header (Figma 4036:28990). */}
      {hasHeader ? (
        <View
          pointerEvents="box-none"
          className="absolute top-0 left-0 flex flex-row items-center gap-3 px-6 py-5"
        >
          {active.senderAvatarUri !== undefined ? (
            <Image
              source={{ uri: active.senderAvatarUri }}
              accessibilityIgnoresInvertColors
              className="rounded-full"
              style={{ width: 40, height: 40 }}
            />
          ) : (
            <View className="w-10 h-10 rounded-full items-center justify-center bg-white/15">
              <Text className="font-sans text-[14px] font-semibold text-white">
                {active.senderInitials ?? '?'}
              </Text>
            </View>
          )}
          <View className="justify-center">
            <Text numberOfLines={1} className="font-sans text-[15px] leading-[20px] font-semibold text-white">
              {active.senderName}
            </Text>
            {active.timestampLabel !== undefined ? (
              <Text numberOfLines={1} className="font-sans text-[13px] leading-[17px] text-white/60">
                {active.timestampLabel}
              </Text>
            ) : null}
          </View>
        </View>
      ) : null}

      {/* Prev / next chevrons (carousel of >1). */}
      {count > 1 ? (
        <>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Previous"
            onPress={(e) => {
              (e as { stopPropagation?: () => void }).stopPropagation?.();
              goPrev();
            }}
            className="absolute left-2 top-1/2 w-11 h-11 -mt-[22px] rounded-full items-center justify-center active:bg-white/10"
          >
            <MediaPreviewIcon name="chevron-left" size={24} color={CHROME} accessibilityLabel="Previous" />
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Next"
            onPress={(e) => {
              (e as { stopPropagation?: () => void }).stopPropagation?.();
              goNext();
            }}
            className="absolute right-2 top-1/2 w-11 h-11 -mt-[22px] rounded-full items-center justify-center active:bg-white/10"
          >
            <MediaPreviewIcon name="chevron-right" size={24} color={CHROME} accessibilityLabel="Next" />
          </Pressable>
        </>
      ) : null}

      {/* Top-right close. */}
      <View pointerEvents="box-none" className="absolute top-0 right-0 p-5">
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={(e) => {
            (e as { stopPropagation?: () => void }).stopPropagation?.();
            onClose();
          }}
          hitSlop={8}
          className="w-11 h-11 rounded-[6px] items-center justify-center active:bg-white/10"
        >
          <MediaPreviewIcon name="close" size={24} color={CHROME} accessibilityLabel="Close" />
        </Pressable>
      </View>

      {/* Bottom-left thumbnail strip (Figma image-group: 60px tall, 2px gap). */}
      {count > 1 ? (
        <View
          pointerEvents="box-none"
          className="absolute bottom-6 left-6 flex flex-row items-center gap-[2px]"
        >
          {items.map((it, i) => (
            <Pressable
              key={it.id}
              accessibilityRole="button"
              accessibilityLabel={`Media ${i + 1}`}
              onPress={(e) => {
                (e as { stopPropagation?: () => void }).stopPropagation?.();
                onIndexChange(i);
                setMenuOpen(false);
              }}
              className={`h-[60px] w-[44px] overflow-hidden ${
                i === safeIndex
                  ? 'rounded-[8px] border-2 border-white'
                  : 'rounded-[6px] opacity-60'
              }`}
            >
              {(it.posterUri ?? it.uri) !== undefined ? (
                <Image
                  source={{ uri: (it.posterUri ?? it.uri) as string }}
                  resizeMode="cover"
                  accessibilityIgnoresInvertColors
                  style={{ width: '100%', height: '100%' }}
                />
              ) : (
                <View className="w-full h-full bg-white/10" />
              )}
            </Pressable>
          ))}
        </View>
      ) : null}

      {/* Bottom-right toolbar (Figma Frame 1000005233). */}
      <View pointerEvents="box-none" className="absolute bottom-6 right-6 flex flex-row items-center gap-1">
        {props.onDownload !== undefined ? (
          <ToolbarButton label="Download" onPress={() => props.onDownload?.(active)}>
            <MediaPreviewIcon name="download" size={24} color={CHROME} accessibilityLabel="Download" />
          </ToolbarButton>
        ) : null}
        {props.onForward !== undefined ? (
          <ToolbarButton label="Forward" onPress={() => props.onForward?.(active)}>
            <MediaPreviewIcon name="forward" size={24} color={CHROME} accessibilityLabel="Forward" />
          </ToolbarButton>
        ) : null}
        {props.onOpenExternal !== undefined ? (
          <ToolbarButton label="Share" onPress={() => props.onOpenExternal?.(active)}>
            <MediaPreviewIcon name="share" size={24} color={CHROME} accessibilityLabel="Share" />
          </ToolbarButton>
        ) : null}
        <View className="relative">
          <ToolbarButton label="More options" active={menuOpen} onPress={() => setMenuOpen((v) => !v)}>
            <MediaPreviewIcon name="more" size={24} color={CHROME} accessibilityLabel="More options" />
          </ToolbarButton>
          {menuOpen ? (
            <View className="absolute bottom-[52px] right-0 min-w-[245px] rounded-[12px] p-1 bg-[#15171C] border border-[#2F333B]">
              {props.onReply !== undefined ? (
                <MenuRow label="Reply" icon="reply" onPress={() => { setMenuOpen(false); props.onReply?.(active); }} />
              ) : null}
              {props.onCopy !== undefined && active.kind === 'image' ? (
                <MenuRow label="Copy image" icon="copy" onPress={() => { setMenuOpen(false); props.onCopy?.(active); }} />
              ) : null}
              {props.onGoToMessage !== undefined ? (
                <MenuRow label="Go to message" icon="goto" onPress={() => { setMenuOpen(false); props.onGoToMessage?.(active); }} />
              ) : null}
              {props.onDelete !== undefined ? (
                <MenuRow label="Delete" icon="trash" destructive onPress={() => { setMenuOpen(false); props.onDelete?.(active); }} />
              ) : null}
            </View>
          ) : null}
        </View>
      </View>
    </Pressable>
  );

  if (IS_WEB) {
    return (
      <WebPortal>
        <View style={StyleSheet.absoluteFill}>{stage}</View>
      </WebPortal>
    );
  }
  return (
    <Modal visible={open} transparent animationType="none" onRequestClose={onClose} accessibilityViewIsModal>
      {stage}
    </Modal>
  );
});

MediaPreviewScreen.displayName = 'MediaPreviewScreen';

// Whether a doc can be shown inline in the browser's built-in viewer
// (iframe). PDFs, text, images and HTML/SVG render; office/archive types
// fall back to a download card. Matched by MIME first, then extension.
const INLINE_DOC_EXTS = [
  'pdf', 'txt', 'text', 'md', 'markdown', 'csv', 'tsv', 'log', 'json',
  'xml', 'html', 'htm', 'svg', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp',
];
function docRendersInline(mime?: string, filename?: string): boolean {
  const m = (mime ?? '').toLowerCase();
  if (m === 'application/pdf' || m.endsWith('/pdf')) return true;
  if (m.startsWith('text/') || m.startsWith('image/')) return true;
  if (m === 'application/json' || m === 'application/xml') return true;
  const ext = (filename ?? '').split('.').pop()?.toLowerCase() ?? '';
  return INLINE_DOC_EXTS.includes(ext);
}

// Inline error card shown on the stage when an image/video URL is
// broken or expired (onError) — avoids a silent blank/black stage.
function MediaLoadError({ noun }: { noun: string }): JSX.Element {
  return (
    <View className="items-center justify-center px-8 py-12 rounded-[12px] bg-[#15171C]">
      <Text className="font-sans text-[15px] leading-[20px] text-white/70 text-center">
        Couldn't load {noun}.
      </Text>
    </View>
  );
}

function ToolbarButton({
  label,
  onPress,
  active,
  children,
}: {
  label: string;
  onPress: () => void;
  active?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={(e) => {
        (e as { stopPropagation?: () => void }).stopPropagation?.();
        onPress();
      }}
      hitSlop={6}
      className={`w-11 h-11 rounded-[6px] items-center justify-center active:bg-white/10 ${
        active === true ? 'bg-[#1A1D24]' : ''
      }`}
    >
      {children}
    </Pressable>
  );
}

function MenuRow({
  label,
  icon,
  onPress,
  destructive,
}: {
  label: string;
  icon: 'reply' | 'copy' | 'goto' | 'trash';
  onPress: () => void;
  destructive?: boolean;
}): JSX.Element {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={(e) => {
        (e as { stopPropagation?: () => void }).stopPropagation?.();
        onPress();
      }}
      className="flex flex-row items-center gap-3 px-3 py-1 rounded-[12px] active:bg-white/[0.06]"
    >
      <MediaPreviewIcon name={icon} size={16} color={destructive === true ? DESTRUCTIVE : CHROME} accessibilityLabel={label} />
      <Text
        className={`font-sans text-[16px] leading-[22px] ${
          destructive === true ? 'text-[#FF5353]' : 'text-[#F5F6F8]'
        }`}
      >
        {label}
      </Text>
    </Pressable>
  );
}
